import React, { createContext, useContext, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import api from '@/api';
import { addToast } from '@heroui/toast';
import { v4 as uuidv4 } from 'uuid';
import { setTokens, clearTokens, getAccessToken, getIdToken, getRefreshToken } from '@/api';
import type { UserProfile } from '@/types';
import { withAvatarCacheBust } from '@/utils/avatarCacheBust';

interface AuthContextType {
    user: UserProfile | null;
    isLoading: boolean;
    isAuthenticated: boolean;
    logout: () => void;
    cart: any[];
    setCart: React.Dispatch<React.SetStateAction<any[]>>;
    login: (tokens: { access: string; id: string; refresh: string }) => Promise<void>;
    guestCartId: string | null;
    clearGuestCartId: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return ctx;
}

export function AuthOnly({ children }: { children: React.ReactNode }) {
    const { isAuthenticated, isLoading } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if (!isLoading && !isAuthenticated) {
            navigate('/login');
        }
    }, [isLoading, isAuthenticated, navigate]);

    if (isLoading || !isAuthenticated) {
        return <div>Loading...</div>;
    }
    return <>{children}</>;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
    const navigate = useNavigate();

    // Helper to get cached user profile from localStorage
    function getCachedUserProfile(): UserProfile | null {
        try {
            const cached = localStorage.getItem('userProfile');
            if (!cached) return null;
            return JSON.parse(cached);
        } catch {
            return null;
        }
    }

    // Guest cart id logic
    function getOrCreateGuestCartId(): string {
        let guestCartId = localStorage.getItem('guestCartId');
        if (!guestCartId) {
            guestCartId = uuidv4();
            localStorage.setItem('guestCartId', guestCartId);
        }
        return guestCartId;
    }

    // Clear guest cart id from localStorage
    function clearGuestCartId(): void {
        localStorage.removeItem('guestCartId');
    }

    // Check if tokens are available and redirect if not
    useEffect(() => {
        const accessToken = getAccessToken();
        const idToken = getIdToken();
        const refreshToken = getRefreshToken();

        // If any of the required tokens are missing, clear all tokens
        // Note: Don't clear guestCartId here - it should persist for guest users
        if (!accessToken || !idToken || !refreshToken) {
            clearTokens();
            // Only redirect if we're not already on the home page
            /* if (router.pathname !== '/') {
                router.replace('/');
            } */
        }
    }, [navigate]);

    const { data: user, isLoading, isError, refetch } = useQuery<UserProfile | null>({
        queryKey: ['authUser'],
        queryFn: async () => {
            const loadProfile = async () => {
                const { data } = await api.get('/user/profile');
                const withFreshAvatar = data ? { ...data, profileImage: withAvatarCacheBust(data.profileImage) } : data;
                // Cache the profile in localStorage
                localStorage.setItem('userProfile', JSON.stringify(withFreshAvatar));
                return withFreshAvatar;
            };
            try {
                return await loadProfile();
            } catch (error: any) {
                const impersonated = localStorage.getItem('impersonate');
                if (!impersonated) return null;
                // Impersonation failed: drop it and fall back to the admin's own session instead of logging out
                localStorage.removeItem('impersonate');
                localStorage.removeItem('userProfile');
                localStorage.removeItem('selectedOrganizerId');
                addToast({
                    title: 'Could not impersonate',
                    description: error?.response?.data?.message || `Unable to load ${impersonated}. You are back in your own account.`,
                    color: 'danger',
                });
                try {
                    return await loadProfile();
                } catch {
                    return null;
                }
            }
        },
        // null would count as fresh cached data and skip the fetch for the whole staleTime
        initialData: getCachedUserProfile() ?? undefined,
        //refetchOnMount: true,
        staleTime: 15 * 60 * 1000, // 15 minutes
        enabled: !!getAccessToken() && !!getIdToken() && !!getRefreshToken(),
    });

    const [cart, setCart] = React.useState<any[]>([]);
    // Initialize guestCartId from localStorage immediately if user is not authenticated
    const [guestCartId, setGuestCartId] = React.useState<string | null>(() => {
        // Only initialize from localStorage if we don't have tokens (guest user)
        const hasTokens = !!getAccessToken() && !!getIdToken() && !!getRefreshToken();
        if (!hasTokens) {
            return getOrCreateGuestCartId();
        }
        return null;
    });

    // On mount, set guestCartId if not authenticated
    React.useEffect(() => {
        if (!user) {
            const id = getOrCreateGuestCartId();
            setGuestCartId(id);
        } else {
            setGuestCartId(null);
            // Clear guest cart ID from localStorage when user logs in
            clearGuestCartId();
        }
    }, [user]);

    const isAuthenticated = Boolean(user && !isError);

    const logout = React.useCallback(() => {
        clearTokens();
        refetch();
        navigate('/login');
    }, [navigate, refetch]);

    const login = React.useCallback(
        async (tokens: { access: string; id: string; refresh: string }) => {
            setTokens(tokens); // This must be called before refetch
            await refetch();
        },
        [refetch]
    );

    const value: AuthContextType = {
        user: user ?? null,
        isLoading: isLoading,
        isAuthenticated,
        logout,
        cart,
        setCart,
        login,
        guestCartId,
        clearGuestCartId,
    };

    // Signed-in visitor whose profile isn't cached yet (e.g. right after starting or stopping impersonation):
    // wait for it instead of flashing the logged-out UI
    const hasTokens = !!getAccessToken() && !!getIdToken() && !!getRefreshToken();
    if (isLoading && !user && hasTokens) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-white dark:bg-nightBg" role="status" aria-label="Loading">
                <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-primary40 dark:border-nightAccent" />
            </div>
        );
    }

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
} 